import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, memo, useCallback } from "react";
import { ReaderChapter } from '@/services/booksApi';
import { useTheme } from '@/contexts/ThemeContext';
import { motion } from "framer-motion";
import { useChapterTree } from '@/hooks/useChapterTree';
import CloseIcon from './icons/CloseIcon';
import {
  READER_SIDE_PANEL_SPRING_TRANSITION,
  READER_SIDE_PANEL_WIDTH_PX,
} from '@/utils/textChatLayout';

interface ChapterListProps {
  chapters: ReaderChapter[];
  currentChapterId: string | undefined;
  onChapterSelect: (chapterId: string) => void;
  variant?: 'popup' | 'panel';
  isOpen?: boolean;
  onClose?: () => void;
  animationClassName?: string;
  popupAlign?: 'start' | 'center';
}

interface ChapterItemProps {
  chapter: ReaderChapter;
  depth: number;
  isDarkMode: boolean;
  isSelected: boolean;
  variant: 'popup' | 'panel';
  onSelect: (id: string) => void;
  itemRef?: React.Ref<HTMLLIElement>;
}

const ChapterItem = memo(({ chapter, depth, isDarkMode, isSelected, variant, onSelect, itemRef }: ChapterItemProps) => (
  <li ref={itemRef}>
    <button
      type="button"
      aria-current={isSelected ? 'true' : undefined}
      className={`w-full cursor-pointer px-4 py-2 text-sm text-left transition-colors duration-150 ${
        variant === 'panel'
          ? `rounded-xl py-2.5 ${
            isSelected
              ? isDarkMode
                ? 'bg-gray-700 text-gray-200'
                : 'bg-gray-200 text-gray-700'
              : isDarkMode
                ? 'text-gray-200 hover:bg-gray-600'
                : 'text-gray-700 hover:bg-gray-200/50'
          }`
          : `rounded-md ${
            isSelected
              ? isDarkMode
                ? 'bg-gray-700 text-gray-200'
                : 'bg-gray-200 text-gray-700'
              : isDarkMode
                ? 'text-gray-200 hover:bg-gray-600'
                : 'text-gray-700 hover:bg-gray-200/50'
          }`
      }`}
      style={{ paddingLeft: `${16 + depth * 14}px` }}
      onClick={() => onSelect(chapter.id)}
    >
      {chapter.title}
    </button>
  </li>
));

ChapterItem.displayName = 'ChapterItem';

const ChapterList: React.FC<ChapterListProps> = memo(({
                                                   chapters,
                                                   currentChapterId,
                                                   onChapterSelect,
                                                   variant = 'popup',
                                                   isOpen = true,
                                                   onClose,
                                                   animationClassName = '',
                                                   popupAlign = 'start',
                                                 }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const chapterTree = useChapterTree(chapters);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const selectedItemRef = useRef<HTMLLIElement | null>(null);
  const hasScrolledToSelectionRef = useRef(false);
  const [listMaxHeightPx, setListMaxHeightPx] = useState<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const handleChapterSelect = useCallback((id: string) => {
    onChapterSelect(id);
  }, [onChapterSelect]);

  const flattenTree = useCallback(() => {
    const flattened: Array<{ chapter: ReaderChapter; depth: number }> = [];
    const walk = (nodes: typeof chapterTree, depth: number) => {
      nodes.forEach((node) => {
        flattened.push({ chapter: node.chapter, depth });
        walk(node.children, depth + 1);
      });
    };
    walk(chapterTree, 0);
    return flattened;
  }, [chapterTree]);

  const chaptersForRender = useMemo(() => flattenTree(), [flattenTree]);

  const [hasOpenedOnce, setHasOpenedOnce] = useState(isOpen);
  useEffect(() => {
    if (isOpen && !hasOpenedOnce) {
      setHasOpenedOnce(true);
    }
  }, [isOpen, hasOpenedOnce]);

  useLayoutEffect(() => {
    if (variant !== 'popup') return;

    const viewportPadding = 12; // keep a little breathing room from the top of the screen

    const recompute = () => {
      // Cancel any pending animation frame to avoid multiple updates in the same frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        const el = containerRef.current;
        if (!el) return;

        const rect = el.getBoundingClientRect();

        // Space from viewport top
        const availableAbove = Math.max(0, rect.bottom - viewportPadding);

        const fiftyDvhPx = window.innerHeight * 0.5;

        // Final max height: never exceed what fits above, and never exceed 50dvh
        setListMaxHeightPx(Math.floor(Math.min(fiftyDvhPx, availableAbove)));
      });
    };

    recompute();

    window.addEventListener("resize", recompute);
    window.addEventListener("orientationchange", recompute);
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      window.removeEventListener("resize", recompute);
      window.removeEventListener("orientationchange", recompute);
    };
  }, [variant]);

  // When the list opens, bring the current chapter into view (centred where possible)
  // without scrolling any ancestor — the reader page must stay where it is.
  useLayoutEffect(() => {
    if (!isOpen) {
      hasScrolledToSelectionRef.current = false;
      return;
    }
    if (hasScrolledToSelectionRef.current) return;

    // The panel defers rendering its list until the first open, so the refs can still be empty here.
    const list = listRef.current;
    const item = selectedItemRef.current;
    if (!list || !item) return;

    // The popup sizes itself in a rAF; wait for that so the centring maths uses the final height.
    if (variant === 'popup' && listMaxHeightPx === null) return;

    // offsetTop rather than getBoundingClientRect: the popup animates in with a scale
    // transform, which would shrink measured distances while scrollTop stays untransformed.
    const itemTop = item.offsetParent === list ? item.offsetTop : item.offsetTop - list.offsetTop;
    const centredTop = itemTop - (list.clientHeight - item.offsetHeight) / 2;
    const maxScrollTop = Math.max(0, list.scrollHeight - list.clientHeight);

    list.scrollTop = Math.max(0, Math.min(centredTop, maxScrollTop));
    hasScrolledToSelectionRef.current = true;
  }, [isOpen, variant, listMaxHeightPx, currentChapterId, chaptersForRender, hasOpenedOnce]);

  const chapterItems = (
    <ul
      ref={listRef}
      className={variant === 'panel'
        ? 'relative min-h-0 grow overflow-y-auto overscroll-y-contain p-3'
        : 'relative overflow-y-auto p-1.5'}
      style={variant === 'popup'
        ? { maxHeight: listMaxHeightPx ? `${listMaxHeightPx}px` : '50dvh' }
        : undefined}
    >
      {chaptersForRender.map(({ chapter, depth }) => (
        <ChapterItem
          key={chapter.id}
          chapter={chapter}
          depth={depth}
          isDarkMode={isDarkMode}
          isSelected={chapter.id === currentChapterId}
          itemRef={chapter.id === currentChapterId ? selectedItemRef : undefined}
          variant={variant}
          onSelect={handleChapterSelect}
        />
      ))}
    </ul>
  );

  if (variant === 'panel') {
    const borderClass = isDarkMode ? 'border-gray-700' : 'border-gray-300';

    return (
      <motion.aside
        id="reader-chapter-panel"
        className={`chapter-list-container fixed top-[calc(4.25rem_+_var(--safe-top))] bottom-0 left-0 flex w-full flex-col overflow-hidden border-r ${borderClass} ${
          isDarkMode ? 'bg-[#1c1c1e] text-white' : 'bg-white text-gray-900'
        }`}
        style={{
          width: READER_SIDE_PANEL_WIDTH_PX,
          maxWidth: READER_SIDE_PANEL_WIDTH_PX,
          pointerEvents: isOpen ? 'auto' : 'none',
          touchAction: 'pan-y',
        }}
        initial={false}
        animate={{ x: isOpen ? 0 : '-100%', opacity: isOpen ? 1 : 0 }}
        transition={READER_SIDE_PANEL_SPRING_TRANSITION}
        aria-label="Chapters"
        aria-hidden={!isOpen}
        inert={!isOpen}
      >
        <div className={`flex h-17 shrink-0 items-center border-b px-4 pt-3 pb-3 ${borderClass}`}>
          <div className="flex w-full items-center justify-between">
            <h2 className="font-[550] text-base">Chapters</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close chapters"
              className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full"
            >
              <CloseIcon size="sm" />
            </button>
          </div>
        </div>
        {hasOpenedOnce && chapterItems}
      </motion.aside>
    );
  }

  return (
    <div
      id="chapter-popup-list"
      ref={containerRef}
      className={`chapter-list-container absolute bottom-full ${
        popupAlign === 'center' ? 'left-1/2 -translate-x-1/2' : 'left-0 md:left-1/2 md:-translate-x-1/2'
      } mb-2 w-max min-w-[min(18rem,calc(100vw-1.5rem))] max-w-[min(36rem,calc(100vw-1.5rem))] overflow-hidden rounded-lg shadow-2xl z-50 border ${animationClassName} ${
        isDarkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'
      }`}
      style={{
        transformOrigin: "bottom center",
        willChange: "transform, opacity"
      }}
    >
      {chapterItems}
    </div>
  );
});

ChapterList.displayName = 'ChapterList';

export default ChapterList;
